// Web-specific glue around @repo/auth-core's Cognito helpers. Browser-only
// concerns live here: building a redirect URI from `window.location`,
// remembering the in-flight PKCE verifier in sessionStorage, and navigating
// the top-level frame to the Hosted UI.

import {
  buildAuthorizeUrl,
  buildLogoutUrl,
  exchangeCodeForTokens as coreExchangeCodeForTokens,
  generatePkcePair,
  generateState,
  refreshTokens as coreRefreshTokens,
  type CognitoConfig,
  type CognitoIdentityProvider,
  type CognitoTokens,
} from "@repo/auth-core";
import { rememberVerifier } from "./pkce";

export type { CognitoTokens };

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
const APP_BASE_URL =
  import.meta.env.VITE_APP_BASE_URL || window.location.origin;

const REDIRECT_URI = `${APP_BASE_URL}/login`;

const cognitoConfig: CognitoConfig = {
  domain: COGNITO_DOMAIN,
  clientId: COGNITO_CLIENT_ID,
};

/**
 * Redirects the browser to the Cognito Hosted UI to start the auth code flow.
 * Stores a PKCE verifier and state in sessionStorage so the /login callback
 * can complete the exchange.
 */
export async function startLogin(
  returnTo: string = "/",
  identityProvider?: CognitoIdentityProvider,
): Promise<void> {
  const { verifier, challenge } = await generatePkcePair();
  const state = generateState();
  rememberVerifier(verifier, state, returnTo);

  const url = buildAuthorizeUrl(cognitoConfig, {
    redirectUri: REDIRECT_URI,
    codeChallenge: challenge,
    state,
    identityProvider,
  });
  window.location.assign(url);
}

export function exchangeCodeForTokens(
  code: string,
  verifier: string,
): Promise<CognitoTokens> {
  return coreExchangeCodeForTokens(cognitoConfig, {
    code,
    verifier,
    redirectUri: REDIRECT_URI,
  });
}

export function refreshTokens(refreshToken: string): Promise<CognitoTokens> {
  return coreRefreshTokens(cognitoConfig, refreshToken);
}

/** Cognito logout endpoint; returns to the app root. */
export function logoutUrl(): string {
  return buildLogoutUrl(cognitoConfig, { logoutRedirectUri: APP_BASE_URL });
}
